import { useEffect, useMemo, useRef, useState } from 'react';

import DropdownArrowIcon from '@/assets/all-icons/dropdown-arrow-icon';
import { useDropdownPopover } from '@/shared-components/dropdown';
import InfoIconArtwork from '@/shared-components/icons/components/public/InfoIconArtwork';

const normalizeSearchValue = (value) => {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

export default function DropdownFormSingleChoice({
  element,
  selectOptions,
  readOnly,
  placeholder,
  value,
  onChange,
  autoComplete = undefined,
  infoIconDescription = '',
}) {
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isInfoIconOpen, setIsInfoIconOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [searchFilter, setSearchFilter] = useState('');
  const isSearchable = Boolean(selectOptions?.searchable);
  const optionsList = Array.isArray(selectOptions?.options) ? selectOptions.options : [];
  const handleOpenChange = (nextOpen) => {
    setIsOpen(nextOpen);
    if (!nextOpen) setSearchFilter('');
  };
  const {
    rootRef,
    triggerRef,
    menuId,
    close,
    toggle,
    handleTriggerKeyDown,
  } = useDropdownPopover({
    open: isOpen,
    onOpenChange: handleOpenChange,
    disabled: readOnly,
    idPrefix: element || 'dropdown-form-single-choice',
  });

  const resolvedOptions = useMemo(() => {
    return optionsList.map((option, index) => {
      if (selectOptions?.type === 'phone_prefix') {
        return {
          key: `${option?.name ?? option?.dialCode ?? index}`,
          label: option?.name ?? option?.dialCode ?? '',
          value: option?.dialCode ?? '',
        };
      }
      if (selectOptions?.type === 'country') {
        return {
          key: option?.name ?? String(index),
          label: option?.name ?? '',
          value: option?.name ?? '',
        };
      }
      if (option && typeof option === 'object') {
        const label = option.label ?? option.name ?? option.value ?? '';
        const value = option.value ?? option.label ?? option.name ?? '';
        return {
          key: option.key ?? `${label}-${index}`,
          label: String(label ?? ''),
          value,
        };
      }
      return {
        key: typeof option === 'string' ? option : `${option}-${index}`,
        label: typeof option === 'string' ? option : String(option ?? ''),
        value: option,
      };
    });
  }, [optionsList, selectOptions?.type]);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchFilter.trim()) {
      return resolvedOptions;
    }
    const filter = normalizeSearchValue(searchFilter.trim());
    return resolvedOptions.filter((option) => normalizeSearchValue(option.label).includes(filter));
  }, [resolvedOptions, searchFilter, isSearchable]);

  const resolveLabelForValue = (currentValue) => {
    const match = resolvedOptions.find((option) => String(option.value) === String(currentValue));
    return match ? match.label : (currentValue ?? '');
  };

  useEffect(() => {
    if (!isSearchable) {
      setInputValue(resolveLabelForValue(value));
      return;
    }
    if (!isOpen && !searchFilter) {
      setInputValue(resolveLabelForValue(value));
    }
  }, [value, isSearchable, isOpen, searchFilter, resolvedOptions]);

  const handleToggle = () => {
    if (readOnly) {
      return;
    }
    toggle();
    if (!isOpen && isSearchable) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (event) => {
    if (readOnly) {
      return;
    }

    const isInputTarget = event.target === inputRef.current;
    const isPrintableKey = event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;

    if (event.key === 'Escape') {
      close({ restoreFocus: true });
      return;
    }

    if (isSearchable && isInputTarget) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (filteredOptions.length > 0) {
          handleOptionSelect(filteredOptions[0]);
        }
      }
      return;
    }

    if (isSearchable && !isInputTarget && isPrintableKey) {
      event.preventDefault();
      const typedValue = event.key;
      setInputValue(typedValue);
      setSearchFilter(typedValue);
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    handleTriggerKeyDown(event);
  };

  const handleInputChange = (event) => {
    if (!isSearchable || readOnly) {
      return;
    }
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setSearchFilter(nextValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option, event) => {
    event?.stopPropagation?.();
    if (readOnly) {
      return;
    }
    const nextValue = option?.value ?? '';
    const nextLabel = option?.label ?? nextValue;
    if (typeof onChange === 'function') {
      onChange(nextValue);
    }
    setInputValue(nextLabel);
    setSearchFilter('');
    close();
  };

  return (
    <>
      <div
        tabIndex={readOnly ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        className={`dropdown dropdown--form dropdown-form-single-choice__container ${readOnly ? "read-only" : ""} ${isOpen ? "is-open" : ""} ${isSearchable ? "dropdown-form-single-choice__container--searchable" : ""}`}
        id={`${element}_error`}
        ref={(node) => {
          rootRef.current = node;
          triggerRef.current = node;
        }}
      >
        <div className="dropdown__trigger dropdown-form-single-choice__selected-option-container">
          <div className="dropdown__text-container">
            <label htmlFor={element} className="dropdown__label dropdown-form-single-choice__label">
              {placeholder}
              {infoIconDescription?.length > 0 && (
                <span
                  onClick={(event) => {
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                    event.preventDefault();
                    setIsInfoIconOpen((prev) => !prev);
                  }}
                  role="button"
                  tabIndex={-1}
                >
                  <InfoIconArtwork
                    className="step-accordeon-step-header__info-icon"
                    open={isInfoIconOpen}
                    header={placeholder}
                    description={infoIconDescription}
                    onClick={() => setIsInfoIconOpen(false)}
                  />
                </span>
              )}
            </label>
            <div className="dropdown__value-row">
              <input
                type="text"
                className="dropdown__value dropdown-form-single-choice__selected-option"
                value={inputValue ?? ''}
                id={element}
                name={element}
                readOnly={!isSearchable || readOnly}
                onChange={handleInputChange}
                autoComplete={autoComplete}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!isOpen && !readOnly) {
                    setIsOpen(true);
                  }
                }}
                ref={inputRef}
              />
            </div>
          </div>

          <div className="dropdown-form-single-choice__open-options-container">
            { !readOnly && <DropdownArrowIcon className="dropdown__indicator dropdown-form-single-choice__open-options" />}
          </div>
        </div>

        <div id={menuId} className="dropdown__menu dropdown-form-single-choice__options-container" role="listbox">
          {filteredOptions.length === 0 ? (
            <p className="dropdown__option dropdown__option--empty dropdown-form-single-choice__option dropdown-form-single-choice__option--empty">
              No matches
            </p>
          ) : (
            filteredOptions.map((option) => (
              <p
                key={option.key}
                className="dropdown__option dropdown-form-single-choice__option"
                role="option"
                aria-selected={String(option.value) === String(value)}
                onClick={(event) => handleOptionSelect(option, event)}
              >
                {option.label}
              </p>
            ))
          )}
        </div>
      </div>
    </>
  );
}
